<script setup lang="ts">
import { ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import {
  analysisMethodList,
  analysisMethodCreate,
  analysisMethodDelete,
} from "@/common/api/rest/analysis/method";

import type { AnalysisType } from "@/common/types/analysis";

import { searchMethodItem } from "@/components";

import {
  svgIcon,
  baseModal,
  baseConfirm,
  baseTooltip,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: AnalysisType | null;
    width?: string;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    isEdit: true,
  },
);

const methods = ref<any[]>([]);

const selected = ref<any>(null);
const inputSearch = ref("");

const showModalAdd = ref(false);
const showDelConfirm = ref(false);

function fetchData() {
  if (!props.data) return;

  analysisMethodList(props.data.account_id, {
    analysis_id: props.data.analysis_id,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        methods.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function add() {
  showModalAdd.value = true;
}

function addSelect(data: any) {
  selected.value = data;
}

function addCancel() {
  inputSearch.value = "";
  selected.value = null;
  showModalAdd.value = false;
}

function addConfirm() {
  if (!props.data || !selected.value) return;

  analysisMethodCreate(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    analysis_id: props.data.analysis_id,
    group_id: selected.value.group_id,
    method_id: selected.value.method_id,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        addCancel();
        fetchData();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function del(method: any) {
  selected.value = method;
  showDelConfirm.value = true;
}

function delCancel() {
  selected.value = null;
  showDelConfirm.value = false;
}

function delConfirm() {
  if (!props.data || !selected.value) return;

  analysisMethodDelete(props.data.account_id, selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        delCancel();
        fetchData();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watchEffect(() => {
  fetchData();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <div class="content">
      <template v-if="methods.length === 0">
        <div class="form-empty">
          {{ t("analysis.texts.empty_method") }}
        </div>
      </template>

      <template v-else>
        <template v-for="(method, index) in methods" :key="index">
          <div class="item">
            <div class="action">
              <span class="action-title"> Método {{ index + 1 }} </span>

              <template v-if="isEdit">
                <base-tooltip position="relative">
                  <template #trigger>
                    <button
                      type="button"
                      class="btn btn-none btn-sm remove"
                      @click="del(method)"
                    >
                      <svg-icon icon="trash" size="1.75rem" />
                    </button>
                  </template>
                  <template #tooltip>
                    {{ t("common.labels.delete") }}
                  </template>
                </base-tooltip>
              </template>
            </div>

            <div class="infos">
              <div class="line">
                <label>{{ t("common.labels.code") }}: </label>
                <span>{{ method?.code }}</span>
              </div>
              <div class="line">
                <label>{{ t("common.labels.name") }}: </label>
                <span>{{ method?.name }}</span>
              </div>

              <template
                v-for="(item, method_index) in method?.metadata"
                :key="method_index"
              >
                <template v-if="item.type === 'RANGE'">
                  <div class="line">
                    <label>
                      {{ item.name }} {{ t("question.labels.minimum") }}:
                    </label>
                    <span> {{ item.min }} {{ item.measure }} </span>
                  </div>
                  <div class="line">
                    <label>
                      {{ item.name }} {{ t("question.labels.maximum") }}:
                    </label>
                    <span> {{ item.max }} {{ item.measure }} </span>
                  </div>
                </template>
                <template v-else>
                  <div class="line">
                    <label>{{ item.name }}: </label>
                    <span>{{ item.answer }}</span>
                  </div>
                </template>
              </template>
            </div>
          </div>
        </template>
      </template>

      <div class="actions">
        <button class="btn btn-md btn-primary" @click="add()">
          {{ t("common.labels.add").toUpperCase() }}
        </button>
      </div>
    </div>

    <base-modal
      :show="showModalAdd"
      :title="
        t('common.labels.add', {
          concat: t('common.labels.method'),
        }).toUpperCase()
      "
      :show-button-confirm="true"
      :button-confirm="t('common.labels.add')"
      :button-close="t('common.labels.cancel')"
      @close="addCancel()"
      @confirm="addConfirm()"
    >
      <template #content>
        <search-method-item
          :account-id="data?.account_id"
          :method-group-id="data?.method_group_id"
          :ids="methods.map((item) => item.method_id)"
          @update-data="addSelect"
        />
      </template>
    </base-modal>

    <base-confirm
      :show="showDelConfirm"
      max-width="400px"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.method'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: selected?.name,
        })
      "
      @cancel="delCancel"
      @confirm="delConfirm"
    />
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-block: 1rem;
  padding-inline: 1.5rem;

  .content {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .item {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 1rem;
      background-color: var(--background-off);
      // box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.1);
      border-bottom: 1px solid var(--border);
      margin-bottom: 1rem;
      padding-bottom: 2.5rem;
    }
  }

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.25rem;
  }

  .search {
    width: 100%;

    .icon {
      cursor: pointer;
      color: var(--text-off);

      &:hover {
        color: var(--primary);
      }
    }
  }

  .border {
    border-block-end: 1px solid var(--border);
    padding-block-end: 0.4rem;
  }

  .form-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    padding-block: 1.5rem;
  }

  .actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-block: 1.5rem;
  }

  .action {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-block-end: 1rem;

    &-title {
      font-size: 1rem;
      font-weight: bold;
      text-transform: uppercase;
    }

    .remove {
      &:hover {
        color: var(--red);
      }
    }
  }

  .infos {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .line {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    background: #f1f1f1;
    padding: 0.5rem;

    label {
      font-weight: normal;
      flex: 1;
    }

    span {
      font-weight: bold;
      flex: 4;
    }
  }
}
</style>
